import { ApsProjectDataRequest, CreateProjectRequest, DashboardProject, FileParameter, Project, ProjectMetadata, ProjectResponse, ProjectsClient, UpdateProjectProfileRequest, UpdateProjectRequest } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQuery } from "@tanstack/react-query";

const projectsClient = new ProjectsClient(undefined, axios);

export const useDashboardProjects = (options?: any) => {
  return useQuery<DashboardProject[]>({
    ...options,
    queryKey: ["dashboardProjects"],
    queryFn: async () => projectsClient.getDashboardProjectsByUserId(),
    placeholderData: [],
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 60 minutes      
  });
};

export const useProject = (projectId: string | undefined, options?: any) => {
  return useQuery<ProjectResponse | null>({
    ...options,
    queryKey: ["project", projectId],
    queryFn: async () => projectId ? projectsClient.getProject(projectId!) : new Promise((resolve) => resolve(null)),
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 60 minutes      
  });
};

export const useCreateProject = (options?: any) => {
  return useMutation<ProjectResponse>({
    ...options,
    mutationFn: (createProjectRequest: CreateProjectRequest) => projectsClient.createProject(createProjectRequest),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["dashboardProjects"], exact: false });
      options?.onSuccess?.();
    },
  });
};

export const useUpdateProjectMetadata = (projectId: string) => {

  return useMutation({
    mutationFn: async (metadata: ProjectMetadata) => {
      const updateRequest = new UpdateProjectRequest({
        projectId: projectId,
        projectMetadata: metadata
      });
      return await projectsClient.updateProjectSettings(updateRequest);
    },
    onSuccess: () => {
      // Invalidate project queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

export const useUpdateProjectProfile = (projectId: string) => {
  return useMutation({
    mutationFn: async (profileId: string) => {
      const updateRequest = new UpdateProjectProfileRequest({
        projectId: projectId,
        profileId: profileId
      });
      return await projectsClient.updateProjectProfile(projectId, updateRequest);
    },
    onSuccess: () => {
      // Invalidate project queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

export const useDeleteProject = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (projectId: string) => projectsClient.deleteProject(projectId),
    onSuccess: async (_data: any, variables: any, _context: any) => {
      await queryClient.resetQueries({ queryKey: ["project", variables.projectId], exact: false });
      await queryClient.resetQueries({ queryKey: ["dashboardProjects"], exact: false });
      options?.onSuccess?.();
    },
  });
};

export const useCreateProjectImage = (projectId: string | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (fileParameter: FileParameter) => projectsClient.uploadProjectImage(projectId!, fileParameter),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["project"], exact: false });
      options?.onSuccess?.();
    },
  });
};

export const useUpdateApsProjectId = (options?: any) => {
  return useMutation<Project>({
    ...options,
    mutationFn: (assignApsProjectIdRequest: ApsProjectDataRequest) => {
      return projectsClient.updateApsProjectId(assignApsProjectIdRequest.projectId as any, assignApsProjectIdRequest);
    },
  });
};
